/**
 * ScreeningPage — Sprint 6
 * Full redesign of the screening console with:
 * - Dual method cards (CSV Upload | Auto-fetch Coming Soon)
 * - Integrated upload form with drag-and-drop
 * - Access code validation
 * - Results table, carbon chart, invalid rows panel
 * All API logic preserved from LegacyApp.
 */
import React, { useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import {
  Upload,
  Zap,
  ShieldCheck,
  FileSpreadsheet,
  BarChart3,
  Leaf,
  BadgeCheck,
  AlertTriangle,
  Lock,
  Download,
  Search,
  Filter,
  Loader2,
  FileWarning,
  Check,
  CheckCircle2,
  Building2,
  Info,
  ChevronRight,
  X,
  Clock,
  Mail,
  Award,
  ShoppingCart,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { joinAutofetchWaitlist } from "../api/client";
import toast from "react-hot-toast";
import "../styles/screening.css";

/* ── Constants ───────────────────────────────────────────────── */
const API_BASE = "";
const MAX_FILE_MB = 20;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const METHODOLOGY_PDF = "/greensetu_methodology_v1.pdf";
// Template uses GST-standard field names (GSTR-1 / GSTR-2B compatible).
// ctin = supplier GSTIN, trdnm = trade name, inum = invoice number,
// idt = invoice date (DD-MM-YYYY), val = total invoice value,
// txval = taxable value (preferred for carbon scoring — excl. GST),
// hsn_sc = HSN/SAC code, iamt/camt/samt = IGST/CGST/SGST,
// pos = place of supply, inv_typ = invoice type (B2B/SEZ etc.)
const TEMPLATE_ROWS = [
  "ctin,trdnm,inum,idt,val,txval,hsn_sc,desc,pos,inv_typ,iamt,camt,samt",
  "27AABCU9603R1ZX,Acme Technologies Pvt Ltd,INV-2024-001,15-04-2024,177000,150000,8471,Laptop & accessories,Maharashtra,B2B,18000,4500,4500",
  "29GGGGG1314R9Z6,Steel Works India,INV-2024-002,10-05-2024,330400,280000,7208,Hot-rolled steel sheets,Karnataka,B2B,50400,0,0",
  "07AABCE0531F1ZV,Fashion House Ltd,INV-2024-003,01-06-2024,112100,95000,6204,Readymade garments,Delhi,B2B,8550,4275,4275",
];

/* ── Helpers ─────────────────────────────────────────────────── */
function getScoreClass(score) {
  if (score >= 80) return "sc-pill sc-pill-green";
  if (score >= 60) return "sc-pill sc-pill-amber";
  return "sc-pill sc-pill-red";
}
function getStatusClass(status) {
  if (status === "Eligible") return "sc-pill sc-pill-green";
  if (status === "Transition Required") return "sc-pill sc-pill-amber";
  return "sc-pill sc-pill-red";
}
function getBarColor(score) {
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#f59e0b";
  return "#f43f5e";
}
function formatINR(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

let _cachedLogo = null;
async function getLogoDataUrl() {
  if (_cachedLogo) return _cachedLogo;
  const res = await fetch("/rs-logo.png");
  if (!res.ok) throw new Error("Logo fetch failed");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => { _cachedLogo = reader.result; resolve(reader.result); };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function generatePDF(result) {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  let logoData = null;
  try { logoData = await getLogoDataUrl(); } catch (e) { console.warn("Logo load failed:", e); }
  if (logoData) doc.addImage(logoData, "PNG", 14, 10, 22, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("GreenSetu Climate Screening Summary", 42, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Owned and operated by Resilient Sustainance Limited (UK)", 42, 26);
  doc.text("Supported by the Resilient Sustainance global sustainability advisory network", 42, 32);
  doc.text("greensetu.rsustain.org", 42, 38);
  doc.setDrawColor(15, 118, 110);
  doc.setLineWidth(0.6);
  doc.line(14, 45, 196, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Executive Summary", 14, 56);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Total records reviewed: ${result.total_rows}`, 14, 66);
  doc.text(`Entities screened: ${result.processed_rows}`, 14, 72);
  let y = 84;
  if ((result.invalid_rows_count || 0) > 0) {
    doc.text(`${result.invalid_rows_count} record(s) were excluded during validation.`, 14, 78, { maxWidth: 182 });
    y = 90;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Screening Results", 14, y);
  y += 10;
  (result.valid_results || []).forEach((item, index) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`${index + 1}. ${item.company_name || "-"}`, 14, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`GSTIN: ${item.gstin || "-"}`, 14, y); y += 6;
    doc.text(`Invoice value: INR ${formatINR(item.total_invoice_amount)}`, 14, y); y += 6;
    doc.text(`Indicative carbon: ${Number(item.total_carbon || 0).toFixed(4)} kg CO2e`, 14, y); y += 6;
    doc.text(`Green Score: ${item.green_score} | ${item.evidence_pack?.transition_status || "-"}`, 14, y); y += 6;
    doc.text(`Reference ID: ${item.evidence_pack?.evidence_id || "-"}`, 14, y); y += 10;
    if (y > 255) { doc.addPage(); y = 20; }
  });
  if (y > 250) { doc.addPage(); y = 20; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Disclaimer", 14, y); y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(
    "This screening output is intended for indicative climate intelligence. It is not a verified GHG inventory, statutory disclosure, or formal ESG rating.",
    14, y, { maxWidth: 182 }
  );
  doc.save("greensetu_climate_screening_summary.pdf");
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_ROWS.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "greensetu_upload_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Sub-components ──────────────────────────────────────────── */

function MethodCard({ icon: Icon, title, description, badge, badgeColor, active, disabled, onClick, children }) {
  return (
    <div
      className={`sc-method-card${active ? " active" : ""}${disabled ? " disabled" : ""}`}
      onClick={disabled ? undefined : onClick}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) onClick(); }}
    >
      <div className="sc-method-card-inner">
        <div className="sc-method-icon">
          <Icon size={28} />
        </div>
        <div className="sc-method-body">
          <div className="sc-method-top">
            <h3 className="sc-method-title">{title}</h3>
            {badge && (
              <span className={`sc-method-badge ${badgeColor || "green"}`}>{badge}</span>
            )}
          </div>
          <p className="sc-method-desc">{description}</p>
          {children}
        </div>
        {!disabled && (
          <ChevronRight size={20} className={`sc-method-arrow${active ? " rotated" : ""}`} />
        )}
      </div>
    </div>
  );
}

function UploadConsole({ onResult, onCandidateInfo }) {
  const [accessCode, setAccessCode] = useState("");
  const [email, setEmail] = useState("");

  // Candidate Company GSTIN — the company being assessed/certified (mandatory)
  const [candidateGstin, setCandidateGstin] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [gstinLookupLoading, setGstinLookupLoading] = useState(false);
  const [gstinLookupDone, setGstinLookupDone] = useState(false);

  const [uploadType, setUploadType] = useState("sales");

  // Period selection: "monthly" = single GSTR month, "annual" = date range
  const curMonth = new Date().toISOString().slice(0, 7);
  const [periodMode, setPeriodMode] = useState("monthly");
  const [periodMonth, setPeriodMonth] = useState(curMonth);   // monthly mode
  const [periodFrom, setPeriodFrom] = useState(() => {        // annual mode — default: Apr of current or previous FY
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-04`;
  });
  const [periodTo, setPeriodTo] = useState(curMonth);         // annual mode end

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-lookup when a valid 15-char GSTIN is typed
  const handleCandidateGstinChange = useCallback(async (raw) => {
    const val = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    setCandidateGstin(val);
    setCandidateName("");
    setGstinLookupDone(false);
    if (val.length === 15) {
      setGstinLookupLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/gstin/lookup?gstin=${encodeURIComponent(val)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.found && data.legal_name) {
            setCandidateName(data.legal_name);
            onCandidateInfo?.({ gstin: val, name: data.legal_name });
            if (data.source === "gst_portal") {
              toast.success(`Company found: ${data.legal_name}`, { duration: 3000 });
            }
          }
        }
      } catch (_) { /* silent */ } finally {
        setGstinLookupLoading(false);
        setGstinLookupDone(true);
      }
    }
  }, [onCandidateInfo]);

  const handleFile = useCallback((chosen) => {
    setError("");
    setSuccess("");
    if (!chosen) return;
    if (chosen.size > MAX_FILE_BYTES) {
      setError(`File too large. Maximum size is ${MAX_FILE_MB} MB.`);
      return;
    }
    if (!chosen.name.match(/\.(csv|xlsx|xls|json)$/i)) {
      setError("Unsupported file type. Please upload a CSV, Excel (.xlsx/.xls), or JSON file.");
      return;
    }
    setFile(chosen);
  }, []);

  const handleFileChange = useCallback((e) => {
    handleFile(e.target.files?.[0] || null);
  }, [handleFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0] || null);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleValidateCode = useCallback(async () => {
    if (!accessCode.trim()) { setError("Please enter an access code."); return; }
    setValidating(true);
    setError(""); setSuccess(""); setLicenseStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/v1/access/validate-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: accessCode.trim(),
          email: email.trim() || undefined,
          gstin: candidateGstin.trim() || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.valid) throw new Error(data.detail || data.message || "Access code validation failed.");
      setLicenseStatus(data);
      setSuccess(`Access code validated. ${data.remaining_analyses ?? 0} analyses remaining.`);
      toast.success(`Code valid — ${data.remaining_analyses ?? 0} runs remaining.`);
    } catch (err) {
      setError(err.message || "Unable to validate access code.");
      toast.error(err.message || "Access code validation failed.");
    } finally {
      setValidating(false);
    }
  }, [accessCode, email, candidateGstin]);

  // Derive the assessment_period string to send (YYYY-MM = start of period)
  const resolvedAssessmentPeriod = periodMode === "annual" ? periodFrom : periodMonth;

  const handleUpload = useCallback(async () => {
    if (!candidateGstin.trim() || candidateGstin.length !== 15) {
      setError("Candidate Company GSTIN is required (15 characters).");
      return;
    }
    if (!file) { setError("Please select a file before proceeding."); return; }
    if (!accessCode.trim()) { setError("Please enter your access code."); return; }
    if (!licenseStatus?.valid) { setError("Please validate your access code before uploading."); return; }
    setLoading(true); setError(""); setSuccess(""); onResult(null);
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams();
    params.append("access_code", accessCode.trim());
    if (email.trim()) params.append("email", email.trim());
    params.append("gstin", candidateGstin.trim());
    if (resolvedAssessmentPeriod) params.append("assessment_period", resolvedAssessmentPeriod);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5 * 60 * 1000);
    try {
      const response = await fetch(`${API_BASE}/api/v1/upload/${uploadType}?${params.toString()}`, {
        method: "POST", body: formData, signal: controller.signal,
      });
      if (!response.ok) {
        let message = `Upload failed (${response.status})`;
        try { const d = await response.json(); message = d.detail || d.message || JSON.stringify(d); }
        catch { message = await response.text(); }
        throw new Error(message);
      }
      const data = await response.json();
      onResult(data);
      const processed = Number(data.processed_rows || 0);
      const next = licenseStatus
        ? { ...licenseStatus, used_count: Number(licenseStatus.used_count || 0) + processed, remaining_analyses: Math.max(0, Number(licenseStatus.remaining_analyses || 0) - processed) }
        : licenseStatus;
      if (next) setLicenseStatus(next);
      setSuccess(`Upload complete. ${processed} entit${processed === 1 ? "y" : "ies"} screened.`);
      toast.success(`Screening complete — ${processed} ${processed === 1 ? "entity" : "entities"} processed.`);
    } catch (err) {
      const msg = err.name === "AbortError"
        ? "Upload timed out after 5 minutes. Try a smaller file."
        : err.message || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg, { duration: 6000 });
    } finally {
      clearTimeout(timeout); setLoading(false);
    }
  }, [file, accessCode, email, candidateGstin, uploadType, resolvedAssessmentPeriod, licenseStatus, onResult]);

  const usagePct = licenseStatus?.valid && licenseStatus.max_analyses
    ? (Number(licenseStatus.used_count || 0) / Number(licenseStatus.max_analyses)) * 100
    : 0;

  return (
    <div className="sc-console">
      {/* Console header */}
      <div className="sc-console-header">
        <div>
          <h2 className="sc-console-title">Upload Screening Data</h2>
          <p className="sc-console-sub">
            Upload GSTR-1 (Sales) or GSTR-2B (Procurement) data for a candidate company to generate
            carbon exposure, Green Score, and transition classification.
          </p>
        </div>
        <div className="sc-secure-chip">
          <ShieldCheck size={14} /> Access-controlled
        </div>
      </div>

      <div className="sc-console-body">
        {/* Left: form */}
        <div className="sc-form-pane">

          {/* ── Step 1: Candidate Company GSTIN (mandatory) ── */}
          <div className="sc-fieldset">
            <div className="sc-fieldset-title">
              <Building2 size={14} /> Candidate Company GSTIN <span className="sc-required-badge">Required</span>
            </div>
            <p className="sc-fieldset-hint">
              GSTIN of the company being assessed. The certificate will be issued in the company's <strong>GST-registered legal name</strong> — verify it carefully before running the screening.
            </p>
            <div className="sc-gstin-lookup-row">
              <input
                className={`sc-input sc-gstin-input${candidateGstin.length === 15 && !gstinLookupLoading ? (candidateName ? " sc-input-valid" : "") : ""}`}
                value={candidateGstin}
                onChange={(e) => handleCandidateGstinChange(e.target.value)}
                placeholder="e.g. 27AABCU9603R1ZX"
                maxLength={15}
                autoComplete="off"
                style={{ fontFamily: "var(--ft-mono)", letterSpacing: "0.05em" }}
              />
              {gstinLookupLoading && (
                <span className="sc-gstin-lookup-status"><Loader2 size={14} className="sc-spin" /> Looking up…</span>
              )}
              {!gstinLookupLoading && gstinLookupDone && (
                candidateName
                  ? (
                    <div className="sc-gstin-found">
                      <CheckCircle2 size={14} />
                      <span>{candidateName}</span>
                    </div>
                  )
                  : <span className="sc-gstin-notfound"><Info size={14} /> Not found in our records — enter the registered name below</span>
              )}
            </div>

            {/* Name confirmation / manual entry */}
            {gstinLookupDone && candidateGstin.length === 15 && (
              <div className="sc-cert-name-block">
                {candidateName ? (
                  <>
                    <div className="sc-cert-name-row">
                      <input
                        className="sc-input sc-cert-name-input"
                        value={candidateName}
                        onChange={(e) => { setCandidateName(e.target.value); onCandidateInfo?.({ gstin: candidateGstin, name: e.target.value }); }}
                        placeholder="Company name as per GST registration"
                      />
                    </div>
                    <p className="sc-cert-name-notice">
                      <AlertTriangle size={12} />
                      This name will appear on the certificate exactly as written. Ensure it matches your <strong>GST-registered legal name</strong>.
                    </p>
                  </>
                ) : (
                  <>
                    <input
                      className="sc-input sc-cert-name-input"
                      placeholder="Company name as per GST registration"
                      onChange={(e) => { setCandidateName(e.target.value); onCandidateInfo?.({ gstin: candidateGstin, name: e.target.value }); }}
                    />
                    <p className="sc-cert-name-notice">
                      <AlertTriangle size={12} />
                      Enter the exact legal name as registered on the GST portal. This will appear on the issued certificate.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Step 2: Data type ── */}
          <div className="sc-fieldset">
            <div className="sc-fieldset-title">
              <FileSpreadsheet size={14} /> Data Type
            </div>
            <select className="sc-input" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
              <option value="sales">GSTR-1 / Sales Register (outward supplies)</option>
              <option value="procurement">GSTR-2B / Procurement Register (inward supplies)</option>
            </select>
          </div>

          {/* ── Step 3: Assessment Period ── */}
          <div className="sc-fieldset">
            <div className="sc-fieldset-title">
              <Clock size={14} /> Assessment Period
            </div>
            <div className="sc-period-toggle">
              <button
                type="button"
                className={`sc-period-tab${periodMode === "monthly" ? " active" : ""}`}
                onClick={() => setPeriodMode("monthly")}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`sc-period-tab${periodMode === "annual" ? " active" : ""}`}
                onClick={() => setPeriodMode("annual")}
              >
                Annual / Custom Range
              </button>
            </div>
            {periodMode === "monthly" ? (
              <div className="sc-period-field">
                <label className="sc-label">GSTR filing month</label>
                <input
                  type="month"
                  className="sc-input"
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  max={curMonth}
                />
              </div>
            ) : (
              <div className="sc-period-range">
                <div className="sc-period-field">
                  <label className="sc-label">From</label>
                  <input
                    type="month"
                    className="sc-input"
                    value={periodFrom}
                    onChange={(e) => setPeriodFrom(e.target.value)}
                    max={periodTo || curMonth}
                  />
                </div>
                <div className="sc-period-sep">→</div>
                <div className="sc-period-field">
                  <label className="sc-label">To</label>
                  <input
                    type="month"
                    className="sc-input"
                    value={periodTo}
                    onChange={(e) => setPeriodTo(e.target.value)}
                    min={periodFrom}
                    max={curMonth}
                  />
                </div>
              </div>
            )}
            <p className="sc-period-note">
              {periodMode === "annual"
                ? "Upload a single file containing all invoice data for this range (e.g. full financial year Apr–Mar)."
                : "Upload data for a single GSTR filing month."}
            </p>
          </div>

          {/* ── Step 4: Access code ── */}
          <div className="sc-fieldset">
            <div className="sc-fieldset-title">
              <Lock size={14} /> Access Code
            </div>
            {!accessCode && !licenseStatus && (
              <div className="sc-access-hint">
                <Info size={13} />
                <span>
                  New user? <Link to="/dashboard" style={{ color: "var(--gs-primary)", fontWeight: 600 }}>Go to Dashboard</Link> → Activate Free Trial to get your access code (2 free runs).
                </span>
              </div>
            )}
            <div className="sc-code-row">
              <input
                className="sc-input"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter your GreenSetu access code"
                onKeyDown={(e) => e.key === "Enter" && handleValidateCode()}
              />
              <button
                className="sc-btn sc-btn-outline"
                onClick={handleValidateCode}
                disabled={validating || loading || !accessCode.trim()}
              >
                {validating ? <><Loader2 size={14} className="sc-spin" /> Validating…</> : <><ShieldCheck size={14} /> Validate</>}
              </button>
            </div>
            <div className="sc-field" style={{ marginTop: "0.75rem" }}>
              <label className="sc-label">Email <span className="sc-optional">(optional)</span></label>
              <input className="sc-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Registered email" />
            </div>
          </div>

          {/* ── Step 5: File upload ── */}
          <div
            className={`sc-dropzone${dragOver ? " drag-over" : ""}${file ? " has-file" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? (
              <div className="sc-file-selected">
                <FileSpreadsheet size={28} className="sc-file-icon" />
                <div>
                  <p className="sc-file-name">{file.name}</p>
                  <p className="sc-file-size">{(file.size / 1024).toFixed(1)} KB · {file.name.split('.').pop().toUpperCase()}</p>
                </div>
                <button
                  className="sc-file-remove"
                  onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <div className="sc-drop-icon"><Upload size={30} /></div>
                <p className="sc-drop-title">Drop file here or <span className="sc-drop-link">browse</span></p>
                <p className="sc-drop-hint">CSV, Excel (.xlsx/.xls), or JSON · GSTR-1 / GSTR-2B columns accepted · Max {MAX_FILE_MB} MB</p>
              </>
            )}
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.json" className="sc-file-input" onChange={handleFileChange} />
          </div>

          {/* Template download */}
          <button className="sc-template-btn" onClick={downloadTemplate}>
            <Download size={14} /> Download CSV Template
          </button>

          {/* Alerts */}
          {error && <div className="sc-alert sc-alert-error"><AlertTriangle size={15} /> {error}</div>}
          {success && <div className="sc-alert sc-alert-success"><Check size={15} /> {success}</div>}

          {/* Upload button */}
          <button
            className="sc-btn sc-btn-primary sc-btn-full"
            onClick={handleUpload}
            disabled={loading || !file || !accessCode.trim() || !licenseStatus?.valid || candidateGstin.length !== 15}
          >
            {loading
              ? <><Loader2 size={16} className="sc-spin" /> Analysing data…</>
              : <><BarChart3 size={16} /> Run Carbon Screening</>
            }
          </button>
        </div>

        {/* Right: entitlement */}
        <div className="sc-entitlement-pane">
          <div className="sc-entitlement-card">
            <div className="sc-ent-header">
              <div>
                <p className="sc-ent-label">Screening entitlement</p>
                <div className="sc-ent-value">
                  {licenseStatus?.valid
                    ? `${licenseStatus.remaining_analyses ?? 0} remaining`
                    : "Validate code"}
                </div>
              </div>
              <div className="sc-ent-icon"><Lock size={22} /></div>
            </div>

            {licenseStatus?.valid && (
              <>
                <div className="sc-ent-bar-wrap">
                  <div className="sc-ent-bar" style={{ width: `${Math.min(100, usagePct)}%` }} />
                </div>
                <div className="sc-ent-usage">
                  <span>{licenseStatus.used_count ?? 0} used</span>
                  <span>{licenseStatus.max_analyses ?? 0} total</span>
                </div>
                <div className="sc-ent-validated">
                  <CheckCircle2 size={14} /> Code validated
                </div>
              </>
            )}
          </div>

          <div className="sc-info-card">
            <p className="sc-info-title">GSTR-1 / GSTR-2B compatible columns</p>
            <div className="sc-col-list">
              {[
                ["ctin / GSTIN", "Supplier GSTIN (15-digit)", true],
                ["txval / val", "Taxable value (txval preferred) or total invoice value (val). txval excludes GST — more accurate for carbon scoring", true],
                ["hsn_sc / hsn_code", "HSN or SAC code. Often blank in GSTR-2B — map from your Purchase Orders", true],
                ["trdnm / lgnm", "Trade Name or Legal Name (GSTR-2B). Also: supplier_name, vendor_name", false],
                ["inum", "Invoice number (GSTR-2B field name). Also: invoice_number", false],
                ["idt", "Invoice date DD-MM-YYYY (GSTR-2B). Also: invoice_date", false],
                ["pos", "Place of Supply — state code. Also: state", false],
                ["inv_typ", "Invoice type: B2B, SEZ, etc. Also: purchase_category", false],
                ["iamt / camt / samt", "IGST / CGST / SGST amounts — stored for ITC reconciliation", false],
                ["desc", "Description of goods/services", false],
              ].map(([col, desc, required]) => (
                <div key={col} className="sc-col-item">
                  <code className="sc-col-name">{col}</code>
                  <span className="sc-col-desc">{desc}</span>
                  {required
                    ? <span className="sc-col-req">Required</span>
                    : <span className="sc-col-opt">Optional</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="sc-method-link">
            <a href={METHODOLOGY_PDF} target="_blank" rel="noreferrer" className="sc-text-link">
              <Info size={14} /> View scoring methodology
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryStats({ result, licenseStatus }) {
  const avgScore = result?.valid_results?.length
    ? Math.round(result.valid_results.reduce((s, r) => s + Number(r.green_score || 0), 0) / result.valid_results.length)
    : null;
  const totalCarbon = result?.valid_results?.reduce((s, r) => s + Number(r.total_carbon || 0), 0) || 0;

  const cards = [
    { label: "Entities screened", value: result?.processed_rows ?? 0, icon: Building2, color: "#1a6b3c", bg: "#dcfce7" },
    { label: "Indicative carbon", value: `${totalCarbon.toFixed(2)} kg`, icon: Leaf, color: "#0d9488", bg: "#ccfbf1" },
    { label: "Average green score", value: avgScore ?? "-", icon: BadgeCheck, color: "#f59e0b", bg: "#fef9c3" },
    { label: "Remaining analyses", value: licenseStatus?.remaining_analyses ?? "-", icon: ShieldCheck, color: "#64748b", bg: "#f1f5f9" },
  ];

  return (
    <div className="sc-stats-grid">
      {cards.map(({ label, value, icon: Icon, color, bg }) => (
        <div key={label} className="sc-stat-card">
          <div className="sc-stat-icon" style={{ background: bg, color }}><Icon size={20} /></div>
          <div className="sc-stat-value">{value}</div>
          <div className="sc-stat-label">{label}</div>
        </div>
      ))}
    </div>
  );
}

function CarbonChart({ result }) {
  const chartData = useMemo(
    () => (result?.valid_results || []).map((item) => ({
      name: item.company_name || item.gstin,
      carbon: Number(item.total_carbon || 0),
      score: Number(item.green_score || 0),
    })),
    [result]
  );
  if (!chartData.length) return null;
  return (
    <div className="sc-card">
      <div className="sc-card-pad">
        <h3 className="sc-card-title">Carbon distribution</h3>
        <div className="sc-chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg CO2e`, "Carbon"]} />
              <Bar dataKey="carbon" radius={[6, 6, 0, 0]}>
                {chartData.map((entry, i) => <Cell key={i} fill={getBarColor(entry.score)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function ResultsTable({ result, onDownload, pdfLoading }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const rows = result?.valid_results || [];
    return rows.filter((item) => {
      const matchQ = [item.company_name, item.gstin, item.evidence_pack?.evidence_id]
        .filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase());
      const matchS = status === "all" || (item.evidence_pack?.transition_status || "") === status;
      return matchQ && matchS;
    });
  }, [result, query, status]);

  if (!result) return null;

  return (
    <div className="sc-card">
      <div className="sc-card-pad">
        <div className="sc-table-head">
          <div>
            <h3 className="sc-card-title">Climate screening results</h3>
            <p className="sc-card-sub">Decision-support output for MSMEs, lenders, and advisors.</p>
          </div>
          <div className="sc-toolbar">
            <div className="sc-search-wrap">
              <Search size={15} className="sc-search-icon" />
              <input
                className="sc-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search entity, GSTIN or reference"
              />
            </div>
            <div className="sc-select-wrap">
              <Filter size={14} />
              <select className="sc-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All classifications</option>
                <option value="Eligible">Eligible</option>
                <option value="Transition Required">Transition Required</option>
              </select>
            </div>
            <button className="sc-btn sc-btn-outline" onClick={onDownload} disabled={pdfLoading}>
              {pdfLoading
                ? <><Loader2 size={14} className="sc-spin" /> Preparing PDF</>
                : <><Download size={14} /> Download PDF</>
              }
            </button>
          </div>
        </div>

        <div className="sc-table-scroll">
          <table className="sc-table">
            <thead>
              <tr>
                <th>GSTIN</th>
                <th>Entity</th>
                <th className="right">Invoice Value</th>
                <th className="right">Carbon</th>
                <th className="center">Green Score</th>
                <th className="center">Classification</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="sc-empty-row">No results match your filter.</td></tr>
              ) : filtered.map((item, idx) => {
                const statusText = item.evidence_pack?.transition_status || "-";
                return (
                  <tr key={`${item.gstin}-${idx}`}>
                    <td className="sc-mono">{item.gstin}</td>
                    <td>
                      <div className="sc-entity-name">{item.company_name || "-"}</div>
                      <div className="sc-entity-sub">Structured screening output</div>
                    </td>
                    <td className="right">₹{formatINR(item.total_invoice_amount)}</td>
                    <td className="right">{Number(item.total_carbon || 0).toFixed(4)} kg</td>
                    <td className="center">
                      <span className={getScoreClass(Number(item.green_score || 0))}>{item.green_score}</span>
                    </td>
                    <td className="center">
                      <span className={getStatusClass(statusText)}>{statusText}</span>
                    </td>
                    <td className="sc-mono sc-small">{item.evidence_pack?.evidence_id || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InvalidRowsPanel({ result }) {
  const rows = result?.invalid_rows || [];
  if (!rows.length) return null;
  return (
    <div className="sc-card sc-card-warning">
      <div className="sc-card-pad">
        <h3 className="sc-card-title sc-with-icon">
          <FileWarning size={17} className="sc-amber" />
          {rows.length} row{rows.length !== 1 ? "s" : ""} excluded during validation
        </h3>
        <div className="sc-table-scroll">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Row</th><th>GSTIN</th><th>Entity</th>
                <th>Invoice Amount</th><th>HSN Code</th><th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.row_number}</td>
                  <td className="sc-mono sc-small">{row.gstin || "-"}</td>
                  <td>{row.company_name || "-"}</td>
                  <td>{row.invoice_amount != null ? `₹${formatINR(row.invoice_amount)}` : "-"}</td>
                  <td>{row.hsn_code || "-"}</td>
                  <td className="sc-reason">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DisclaimerBar() {
  return (
    <div className="sc-disclaimer">
      <Info size={15} className="sc-disclaimer-icon" />
      <p>
        <strong>Methodological note:</strong> GreenSetu provides screening-level climate intelligence
        derived from structured transaction data. Outputs are indicative and do not constitute verified
        GHG inventories, regulatory disclosures, or ESG ratings.
        &nbsp;<a href={METHODOLOGY_PDF} target="_blank" rel="noreferrer" className="sc-text-link">View full methodology →</a>
      </p>
    </div>
  );
}

/* ── Certificate issuance panel ─────────────────────────────── */
function CertificatePanel({ salesBatchId, procurementBatchId, candidateGstin, candidateName }) {
  const [issuing, setIssuing] = useState(false);
  const [certResult, setCertResult] = useState(null);
  const [certError, setCertError] = useState("");
  const token = localStorage.getItem("gs_token");

  const canIssue = salesBatchId || procurementBatchId;
  const isComprehensive = salesBatchId && procurementBatchId;

  const handleIssue = useCallback(async () => {
    if (!canIssue) return;
    setIssuing(true); setCertError(""); setCertResult(null);
    try {
      const body = isComprehensive
        ? { batch_id: salesBatchId, procurement_batch_id: procurementBatchId }
        : salesBatchId
          ? { batch_id: salesBatchId }
          : { batch_id: procurementBatchId };

      const res = await fetch(`${API_BASE}/api/v1/certificates/issue`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Certificate issuance failed.");
      setCertResult(data);
      toast.success("Certificate issued successfully!");
    } catch (err) {
      const msg = err.message || "Failed to issue certificate.";
      setCertError(msg);
      toast.error(msg);
    } finally {
      setIssuing(false);
    }
  }, [canIssue, isComprehensive, salesBatchId, procurementBatchId, token]);

  if (!canIssue) return null;

  return (
    <div className="sc-card sc-cert-panel">
      <div className="sc-card-pad">
        <div className="sc-cert-header">
          <Award size={22} className="sc-cert-icon" />
          <div>
            <h3 className="sc-card-title">Issue Certificate</h3>
            {candidateName && (
              <p className="sc-card-sub" style={{ fontWeight: 600, color: "var(--gs-slate)", marginBottom: "0.25rem" }}>
                {candidateName}
                {candidateGstin && <span style={{ fontFamily: "var(--ft-mono)", fontWeight: 400, fontSize: "0.78rem", color: "var(--gs-muted)", marginLeft: "0.5rem" }}>({candidateGstin})</span>}
              </p>
            )}
            <p className="sc-card-sub">
              {isComprehensive
                ? "Both sales and procurement data uploaded — issue a Comprehensive Supply Chain Carbon Certificate."
                : salesBatchId
                  ? "Sales data uploaded — issue a Supply Chain Carbon Certificate."
                  : "Procurement data uploaded — issue a Supply Chain Carbon Certificate."}
            </p>
          </div>
        </div>

        <div className="sc-cert-batches">
          {salesBatchId && (
            <div className="sc-cert-batch sc-cert-batch-sales">
              <TrendingUp size={14} /> Sales data ready
            </div>
          )}
          {procurementBatchId && (
            <div className="sc-cert-batch sc-cert-batch-proc">
              <ShoppingCart size={14} /> Procurement data ready
            </div>
          )}
          {!procurementBatchId && salesBatchId && (
            <div className="sc-cert-batch sc-cert-batch-hint">
              + Upload procurement data for a Comprehensive Certificate
            </div>
          )}
          {!salesBatchId && procurementBatchId && (
            <div className="sc-cert-batch sc-cert-batch-hint">
              + Upload sales data for a Comprehensive Certificate
            </div>
          )}
        </div>

        {certError && (
          <div className="sc-alert sc-alert-error"><AlertTriangle size={15} /> {certError}</div>
        )}

        {certResult ? (
          <div className="sc-cert-issued">
            <div className="sc-cert-issued-id">
              <CheckCircle2 size={18} className="sc-cert-check" />
              <span>Certificate ID: <strong>{certResult.certificate_id}</strong></span>
            </div>
            <div className="sc-cert-issued-meta">
              Valid {certResult.issue_date} → {certResult.expiry_date}
            </div>
            <a
              href={`${API_BASE}/api/v1/certificates/${certResult.certificate_id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="sc-btn sc-btn-primary"
            >
              <Download size={15} /> Download Certificate PDF
            </a>
          </div>
        ) : (
          <button
            className="sc-btn sc-btn-primary"
            onClick={handleIssue}
            disabled={issuing || !token}
          >
            {issuing
              ? <><Loader2 size={15} className="sc-spin" /> Issuing…</>
              : <><Award size={15} /> Issue {isComprehensive ? "Comprehensive " : ""}Certificate</>
            }
          </button>
        )}
        {!token && (
          <p className="sc-cert-login-hint">
            <Link to="/login" className="sc-text-link">Log in</Link> to issue certificates.
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */
export default function ScreeningPage() {
  const [method, setMethod] = useState(null); // 'csv' | 'autofetch'
  const [result, setResult] = useState(null);
  const [salesBatchId, setSalesBatchId] = useState(null);
  const [procurementBatchId, setProcurementBatchId] = useState(null);
  const [candidateGstin, setCandidateGstin] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistGstin, setWaitlistGstin] = useState("");
  const [waitlistSent, setWaitlistSent] = useState(false);
  const [waitlistMsg, setWaitlistMsg] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");
  const consoleRef = useRef(null);

  const handleSelectMethod = useCallback((m) => {
    setMethod(prev => prev === m ? null : m);
    setResult(null);
    setTimeout(() => consoleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  }, []);

  const handleResult = useCallback((data) => {
    setResult(data);
    if (data) {
      // Store batch IDs by upload type
      if (data.batch_id) {
        if (data.upload_type === "procurement") {
          setProcurementBatchId(data.batch_id);
        } else {
          setSalesBatchId(data.batch_id);
        }
      }
      setTimeout(() => {
        document.getElementById("sc-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, []);

  const handleDownloadPDF = useCallback(async () => {
    if (!result || pdfLoading) return;
    setPdfLoading(true);
    try { await generatePDF(result); }
    catch (e) { console.error("PDF generation failed:", e); alert("PDF generation failed. Please try again."); }
    finally { setPdfLoading(false); }
  }, [result, pdfLoading]);

  return (
    <div className="sc-page">
      <Navbar />

      <main className="sc-main">
        <div className="sc-container">

          {/* ── Page header ── */}
          <div className="sc-page-header">
            <div className="sc-breadcrumb">
              <Link to="/dashboard" className="sc-breadcrumb-link">Dashboard</Link>
              <span className="sc-breadcrumb-sep">/</span>
              <span>Screening Console</span>
            </div>
            <h1 className="sc-page-title">Screening Console</h1>
            <p className="sc-page-sub">
              Choose how you want to provide data for your climate screening run.
            </p>
          </div>

          {/* ── Method selection ── */}
          <div className="sc-method-grid">
            <MethodCard
              icon={Upload}
              title="Upload GSTR Data"
              description="Upload your GSTR-1 (Sales) or GSTR-2B (Procurement) data. Standard GST portal column names accepted — CSV, Excel, or JSON."
              badge="Available"
              badgeColor="green"
              active={method === "csv"}
              onClick={() => handleSelectMethod("csv")}
            >
              <div className="sc-method-cols">
                <span>ctin / GSTIN</span>
                <span>txval / val</span>
                <span>hsn_sc</span>
                <span>trdnm / lgnm</span>
              </div>
            </MethodCard>

            <MethodCard
              icon={Zap}
              title="Auto-fetch from GST Portal"
              description="Enter your GSTIN and we fetch the last 12 months of invoice data automatically."
              badge="Coming Soon"
              badgeColor="amber"
              active={method === "autofetch"}
              onClick={() => handleSelectMethod("autofetch")}
            >
              <div className="sc-method-launch">
                <Clock size={13} /> Expected launch: Q3 2026
              </div>
            </MethodCard>
          </div>

          {/* ── CSV Upload console ── */}
          {method === "csv" && (
            <div ref={consoleRef} className="sc-console-wrap">
              <UploadConsole
                onResult={handleResult}
                onLicenseStatus={setLicenseStatus}
                onCandidateInfo={({ gstin, name }) => { setCandidateGstin(gstin); setCandidateName(name); }}
              />
            </div>
          )}

          {/* ── Auto-fetch waitlist ── */}
          {method === "autofetch" && (
            <div ref={consoleRef} className="sc-waitlist-wrap">
              <div className="sc-waitlist-card">
                <div className="sc-waitlist-icon"><Zap size={32} /></div>
                <h2 className="sc-waitlist-title">GST Portal Auto-fetch</h2>
                <p className="sc-waitlist-desc">
                  We're building direct integration with the GST Portal API. Once live, you'll be able
                  to enter your GSTIN and automatically pull the last 12 months of invoice data for
                  carbon screening — no manual CSV export needed.
                </p>
                <div className="sc-waitlist-features">
                  {[
                    "Auto-fetch 12 months of invoice data",
                    "GSTIN-level carbon screening in seconds",
                    "No manual data preparation required",
                    "Supports multiple GSTINs per organisation",
                  ].map((f) => (
                    <div key={f} className="sc-waitlist-feature">
                      <CheckCircle2 size={16} /> {f}
                    </div>
                  ))}
                </div>
                {!waitlistSent ? (
                  <div className="sc-waitlist-form">
                    <input
                      className="sc-input"
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      placeholder="Your email address"
                    />
                    <input
                      className="sc-input"
                      type="text"
                      value={waitlistGstin}
                      onChange={(e) => setWaitlistGstin(e.target.value.toUpperCase())}
                      placeholder="GSTIN (optional)"
                      maxLength={15}
                    />
                    {waitlistError && (
                      <div className="sc-alert sc-alert-error" style={{ marginTop: 0 }}>
                        <X size={14} /> {waitlistError}
                      </div>
                    )}
                    <button
                      className="sc-btn sc-btn-accent"
                      onClick={async () => {
                        if (!waitlistEmail.trim()) return;
                        setWaitlistLoading(true);
                        setWaitlistError("");
                        try {
                          const res = await joinAutofetchWaitlist(
                            waitlistEmail.trim(),
                            waitlistGstin.trim() || null,
                          );
                          setWaitlistMsg(res.data.message);
                          setWaitlistSent(true);
                        } catch (err) {
                          setWaitlistError(
                            err.response?.data?.detail || "Failed to join waitlist. Please try again."
                          );
                        } finally {
                          setWaitlistLoading(false);
                        }
                      }}
                      disabled={!waitlistEmail.trim() || waitlistLoading}
                    >
                      {waitlistLoading
                        ? <><Loader2 size={14} className="sc-spin" /> Joining…</>
                        : <><Mail size={15} /> Join Waitlist</>
                      }
                    </button>
                  </div>
                ) : (
                  <div className="sc-alert sc-alert-success">
                    <Check size={15} /> {waitlistMsg || `You're on the waitlist! We'll notify you at ${waitlistEmail}.`}
                  </div>
                )}
                <p className="sc-waitlist-eta">
                  <Clock size={13} /> Expected launch: Q3 2026
                </p>
              </div>
            </div>
          )}

          {/* ── Results section ── */}
          {result && (
            <div id="sc-results" className="sc-results-section">
              <div className="sc-results-header">
                <h2 className="sc-results-title">Screening Results</h2>
                <p className="sc-results-sub">
                  {result.processed_rows} entit{result.processed_rows === 1 ? "y" : "ies"} screened
                  {result.invalid_rows_count > 0 && ` · ${result.invalid_rows_count} rows excluded`}
                  {result.upload_type === "procurement" ? " · GSTR-2B Procurement" : " · GSTR-1 Sales"}
                  {result.assessment_period && ` · ${new Date(result.assessment_period + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}`}
                </p>
              </div>

              <SummaryStats result={result} licenseStatus={licenseStatus} />
              <CarbonChart result={result} />
              <ResultsTable result={result} onDownload={handleDownloadPDF} pdfLoading={pdfLoading} />
              <InvalidRowsPanel result={result} />
              <CertificatePanel
                salesBatchId={salesBatchId}
                procurementBatchId={procurementBatchId}
                candidateGstin={candidateGstin}
                candidateName={candidateName}
              />
            </div>
          )}

          <DisclaimerBar />
        </div>
      </main>
    </div>
  );
}
